import { useGetEconomatDashboard } from "@/lib/backoffice-hooks";
import { formatDateShort } from "@/lib/format";
import EconomatShell from "@/components/layout/EconomatShell";
import { Card } from "@/components/ui/card";
import { Loader2, Wallet, TrendingUp, School, Hourglass } from "lucide-react";

function fcfa(n: number) { return new Intl.NumberFormat("fr-FR").format(n) + " FCFA"; }
const STATUS_LABEL: Record<string, string> = {
  SUCCESS: "Encaissé", PROCESSING: "En cours", PENDING: "En attente",
  FAILED: "Échoué", CANCELLED: "Annulé", REFUNDED: "Remboursé",
};

export default function EconomatDashboard() {
  const { data, isLoading } = useGetEconomatDashboard();
  if (isLoading) {
    return <EconomatShell><div className="flex items-center justify-center py-32"><Loader2 className="w-8 h-8 animate-spin text-navy" /></div></EconomatShell>;
  }
  if (!data) {
    return (
      <EconomatShell>
        <Card className="p-8 border border-amber-200 bg-amber-50 text-amber-900">
          <h2 className="font-bold mb-1">Tableau de bord diocésain indisponible</h2>
          <p className="text-sm">Impossible de charger le tableau de bord. Veuillez réessayer.</p>
        </Card>
      </EconomatShell>
    );
  }
  return (
    <EconomatShell>
      <div className="space-y-6">
        <header>
          <div className="text-xs uppercase tracking-wider text-slate-500">Diocèse</div>
          <h1 className="text-2xl font-bold text-navy" data-testid="text-diocese-name">{data.dioceseNom}</h1>
          <div className="text-sm text-slate-600">Pilotage opérationnel multi-écoles</div>
        </header>

        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
          <Stat icon={Wallet} label="Total encaissé (diocèse)" value={fcfa(data.totalEncaisseXof)} accent="text-emerald-600" testId="stat-total" />
          <Stat icon={TrendingUp} label="Paiements réussis" value={String(data.nbPaiementsSuccess)} sub={`${data.nbPaiementsPending} en attente`} testId="stat-success" />
          <Stat icon={School} label="Écoles actives" value={`${data.nbEcolesActives} / ${data.nbEcolesTotal}`} testId="stat-ecoles" />
          <Stat icon={Hourglass} label="En attente" value={String(data.nbPaiementsPending)} accent="text-amber-600" testId="stat-pending" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <Card className="p-5 border border-slate-200 shadow-none">
            <h2 className="text-sm font-semibold text-navy mb-3">Top 10 écoles (encaissements)</h2>
            <div className="space-y-2">
              {data.topEcoles.length === 0 && <div className="text-sm text-slate-500">Aucun encaissement.</div>}
              {data.topEcoles.map((e) => (
                <div key={e.ecoleId} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                  <div className="font-medium text-slate-800 truncate pr-2">{e.denomination}</div>
                  <div className="flex items-center gap-4 shrink-0">
                    <span className="text-slate-500 text-xs" style={{ fontFamily: "JetBrains Mono, monospace" }}>{e.count}</span>
                    <span className="font-mono text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(e.totalXof)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
          <Card className="p-5 border border-slate-200 shadow-none">
            <h2 className="text-sm font-semibold text-navy mb-3">Par mode de paiement</h2>
            <div className="space-y-2">
              {data.parMethode.length === 0 && <div className="text-sm text-slate-500">Aucun encaissement.</div>}
              {data.parMethode.map((m) => (
                <div key={m.key} className="flex items-center justify-between text-sm border-b border-slate-100 pb-1.5">
                  <div className="font-medium text-slate-800">{m.label.replace(/_/g, " ")}</div>
                  <div className="flex items-center gap-4">
                    <span className="text-slate-500 text-xs" style={{ fontFamily: "JetBrains Mono, monospace" }}>{m.count}</span>
                    <span className="font-mono text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(m.totalXof)}</span>
                  </div>
                </div>
              ))}
            </div>
          </Card>
        </div>

        <Card className="p-5 border border-slate-200 shadow-none">
          <h2 className="text-sm font-semibold text-navy mb-3">Derniers paiements du diocèse</h2>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-xs uppercase tracking-wide text-slate-500 border-b border-slate-200">
                <tr>
                  <th className="text-left py-2">Référence</th>
                  <th className="text-left">École</th>
                  <th className="text-left">Élève</th>
                  <th className="text-left">Motif</th>
                  <th className="text-right">Montant</th>
                  <th className="text-left pl-3">Statut</th>
                  <th className="text-left">Date</th>
                </tr>
              </thead>
              <tbody>
                {data.recentPaiements.length === 0 && <tr><td colSpan={7} className="py-6 text-center text-slate-500">Aucun paiement.</td></tr>}
                {data.recentPaiements.map((p) => (
                  <tr key={p.id} className="border-b border-slate-100 hover:bg-slate-50">
                    <td className="py-2 font-mono text-xs text-navy" style={{ fontFamily: "JetBrains Mono, monospace" }}>{p.reference}</td>
                    <td className="text-slate-700 truncate max-w-[200px]">{p.ecoleDenomination}</td>
                    <td>{p.eleveNom}</td>
                    <td className="text-slate-600">{p.motifLibelle}</td>
                    <td className="text-right font-mono" style={{ fontFamily: "JetBrains Mono, monospace" }}>{fcfa(p.montantXof)}</td>
                    <td className="pl-3"><span className={`text-xs px-2 py-0.5 rounded ${p.status === "SUCCESS" ? "bg-emerald-100 text-emerald-700" : p.status === "FAILED" || p.status === "CANCELLED" ? "bg-red-100 text-red-700" : "bg-amber-100 text-amber-700"}`}>{STATUS_LABEL[p.status] ?? p.status}</span></td>
                    <td className="text-xs text-slate-500">{formatDateShort(p.paidAt ?? p.createdAt)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      </div>
    </EconomatShell>
  );
}

function Stat({ icon: Icon, label, value, sub, accent, testId }: { icon: any; label: string; value: string; sub?: string; accent?: string; testId?: string }) {
  return (
    <Card className="p-4 border border-slate-200 shadow-none" data-testid={testId}>
      <div className="flex items-center justify-between mb-1.5">
        <div className="text-xs uppercase tracking-wider text-slate-500 font-medium">{label}</div>
        <Icon className={`w-4 h-4 ${accent ?? "text-navy"}`} />
      </div>
      <div className={`text-xl font-bold ${accent ?? "text-navy"}`} style={{ fontFamily: "JetBrains Mono, monospace" }}>{value}</div>
      {sub && <div className="text-xs text-slate-500 mt-0.5">{sub}</div>}
    </Card>
  );
}
